import { isAssessmentTaskStatus } from "../utils/assessment_task_status.util";
import { isCalendarDate } from "../utils/assessment_task_date.util";
import { requireTaskSummary } from "./assessment_tasks.dto";
import type {
    AssessmentWorkItem, AssessmentWorkItemAssessmentOption, AssessmentWorkItemAssigneeOption,
    AssessmentWorkItemCount, AssessmentWorkItemEvidenceDetail, AssessmentWorkItemEvidenceSummary,
    AssessmentWorkItemOffsetPage, AssessmentWorkItemPage, AssessmentWorkItemStatusOption,
    AssessmentWorkItemStatusSelector
} from "./assessment_work_items.types";
import { isEvidenceWorkItemStatus, workItemIdentity } from "./assessment_work_items.types";

export class AssessmentWorkItemApiError extends Error {
    constructor(message: string, readonly code: string | null = null) {
        super(message);
        this.name = "AssessmentWorkItemApiError";
    }
}

function record(value: unknown, name: string): Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value as Record<string, unknown>;
}

function positiveInteger(value: unknown, name: string): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value;
}

function nonnegativeInteger(value: unknown, name: string): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value;
}

function nonemptyString(value: unknown, name: string): string {
    if (typeof value !== "string" || !value.trim()) {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value;
}

function booleanValue(value: unknown, name: string): boolean {
    if (typeof value !== "boolean") {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value;
}

function timestamp(value: unknown, name: string): string | null {
    if (value === null) return null;
    if (typeof value !== "string" ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) ||
        !Number.isFinite(Date.parse(value))) {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value;
}

function evidenceDueDate(value: unknown): string | null {
    if (value === null) return null;
    if (!isCalendarDate(value)) {
        throw new AssessmentWorkItemApiError("Invalid evidence due date response.");
    }
    return value;
}

function arrayOf<T>(value: unknown, name: string, parse: (entry: unknown) => T): T[] {
    if (!Array.isArray(value)) {
        throw new AssessmentWorkItemApiError(`Invalid ${name} response.`);
    }
    return value.map(parse);
}

export function isWorkItemStatusSelector(value: unknown): value is AssessmentWorkItemStatusSelector {
    if (typeof value !== "string") return false;
    const separator = value.indexOf(":");
    if (separator < 0) return false;
    const kind = value.slice(0, separator);
    const status = value.slice(separator + 1);
    return kind === "task" ? isAssessmentTaskStatus(status)
        : kind === "evidence" && isEvidenceWorkItemStatus(status);
}

export function requireWorkItemApiData(response: unknown, fallbackMessage: string): unknown {
    const envelope = record(response, "work-item API");
    if (envelope["success"] !== true) {
        const message = typeof envelope["message"] === "string" && envelope["message"].trim()
            ? envelope["message"] : fallbackMessage;
        const code = typeof envelope["code"] === "string" ? envelope["code"] : null;
        throw new AssessmentWorkItemApiError(message, code);
    }
    if (envelope["data"] === undefined || envelope["data"] === null) {
        throw new AssessmentWorkItemApiError("Invalid work-item API response.");
    }
    return envelope["data"];
}

function requireEvidenceSummary(value: unknown): AssessmentWorkItemEvidenceSummary {
    const evidence = record(value, "evidence work item");
    const status = evidence["status"];
    if (!isEvidenceWorkItemStatus(status)) {
        throw new AssessmentWorkItemApiError("Invalid evidence status response.");
    }
    const capabilities = record(evidence["capabilities"], "evidence capabilities");
    const linkedSecureStorageFileId = evidence["linkedSecureStorageFileId"] === null ? null
        : positiveInteger(evidence["linkedSecureStorageFileId"], "linked file ID");
    const linkedFileName = evidence["linkedFileName"] === null ? null
        : nonemptyString(evidence["linkedFileName"], "linked file name");
    const linkedFileAccessible = booleanValue(evidence["linkedFileAccessible"], "linked file access");
    if ((linkedSecureStorageFileId !== null) !== linkedFileAccessible ||
        (linkedFileAccessible && (capabilities["canUpload"] !== true || linkedFileName === null))) {
        throw new AssessmentWorkItemApiError("Invalid linked file access response.");
    }
    return {
        linkedSecureStorageFileId, linkedFileName, linkedFileAccessible,
        id: positiveInteger(evidence["id"], "evidence ID"),
        productLicenseId: positiveInteger(evidence["productLicenseId"], "evidence assessment ID"),
        merchantId: positiveInteger(evidence["merchantId"], "evidence merchant ID"),
        assessmentLabel: nonemptyString(evidence["assessmentLabel"], "assessment label"),
        merchantLabel: nonemptyString(evidence["merchantLabel"], "merchant label"),
        evidenceName: nonemptyString(evidence["evidenceName"], "evidence name"),
        status,
        dueDate: evidenceDueDate(evidence["dueDate"]),
        assignees: arrayOf(evidence["assignees"], "evidence assignees", (entry) => {
            const assignee = record(entry, "evidence assignee");
            return {
                userId: positiveInteger(assignee["userId"], "evidence assignee ID"),
                displayName: nonemptyString(assignee["displayName"], "evidence assignee name"),
                assignedAt: assignee["assignedAt"] === undefined ? null : timestamp(assignee["assignedAt"], "evidence assignment time"),
                assignedBy: assignee["assignedBy"] == null ? null : (() => {
                    const actor = record(assignee["assignedBy"], "evidence assigning user");
                    return { userId: positiveInteger(actor["userId"], "evidence assigning user ID"),
                        displayName: nonemptyString(actor["displayName"], "evidence assigning user name") };
                })()
            };
        }),
        requirements: arrayOf(evidence["requirements"], "evidence requirements", (entry) => {
            const requirement = record(entry, "evidence requirement");
            return {
                sectionKey: nonemptyString(requirement["sectionKey"], "evidence section key"),
                requirementKey: nonemptyString(requirement["requirementKey"], "evidence requirement key")
            };
        }),
        createdAt: timestamp(evidence["createdAt"], "evidence creation time"),
        updatedAt: timestamp(evidence["updatedAt"], "evidence update time"),
        capabilities: {
            canUpload: booleanValue(capabilities["canUpload"], "evidence upload capability"),
            canEditEvidence: booleanValue(capabilities["canEditEvidence"], "evidence edit capability"),
            canEditEvidenceDetails: booleanValue(capabilities["canEditEvidenceDetails"], "evidence detail edit capability"),
            canDeleteEvidence: booleanValue(capabilities["canDeleteEvidence"], "evidence delete capability"),
            canEmailEvidence: booleanValue(capabilities["canEmailEvidence"], "evidence email capability")
        }
    };
}

export function requireWorkItem(value: unknown): AssessmentWorkItem {
    const row = record(value, "work item");
    if (row["kind"] === "task" && !Object.prototype.hasOwnProperty.call(row, "evidence")) {
        return { kind: "task", task: requireTaskSummary(row["task"]) };
    }
    if (row["kind"] === "evidence" && !Object.prototype.hasOwnProperty.call(row, "task")) {
        return { kind: "evidence", evidence: requireEvidenceSummary(row["evidence"]) };
    }
    throw new AssessmentWorkItemApiError("Invalid work-item kind or source response.");
}

function uniqueItems<T>(items: T[], identity: (item: T) => string, name: string): T[] {
    const seen = new Set<string>();
    for (const item of items) {
        const id = identity(item);
        if (seen.has(id)) throw new AssessmentWorkItemApiError(`Duplicate ${name} response.`);
        seen.add(id);
    }
    return items;
}

export function requireWorkItemPage(value: unknown): AssessmentWorkItemPage {
    const page = record(value, "work-item page");
    const items = uniqueItems(arrayOf(page["items"], "work-item page items", requireWorkItem),
        workItemIdentity, "work item");
    const pageSize = positiveInteger(page["pageSize"], "work-item page size");
    const total = nonnegativeInteger(page["total"], "work-item page total");
    if (items.length > pageSize || items.length > total) {
        throw new AssessmentWorkItemApiError("Invalid work-item page response.");
    }
    return { items, page: positiveInteger(page["page"], "work-item page number"), pageSize, total };
}

function requireOffsetPage<T>(value: unknown, parse: (entry: unknown) => T,
    identity: (item: T) => string): AssessmentWorkItemOffsetPage<T> {
    const page = record(value, "work-item options page");
    const items = uniqueItems(arrayOf(page["items"], "work-item options", parse), identity, "work-item option");
    const limit = positiveInteger(page["limit"], "work-item options limit");
    const total = nonnegativeInteger(page["total"], "work-item options total");
    if (items.length > limit || items.length > total) {
        throw new AssessmentWorkItemApiError("Invalid work-item options page response.");
    }
    return { items, offset: nonnegativeInteger(page["offset"], "work-item options offset"), limit, total };
}

export function requireWorkItemAssessmentOptions(value: unknown):
    AssessmentWorkItemOffsetPage<AssessmentWorkItemAssessmentOption> {
    return requireOffsetPage(value, (entry) => {
        const option = record(entry, "assessment option");
        return {
            productLicenseId: positiveInteger(option["productLicenseId"], "assessment option ID"),
            label: nonemptyString(option["label"], "assessment option label")
        };
    }, (option) => String(option.productLicenseId));
}

export function requireWorkItemAssigneeOptions(value: unknown):
    AssessmentWorkItemOffsetPage<AssessmentWorkItemAssigneeOption> {
    return requireOffsetPage(value, (entry) => {
        const option = record(entry, "assignee option");
        return {
            userId: positiveInteger(option["userId"], "assignee option ID"),
            label: nonemptyString(option["label"], "assignee option label")
        };
    }, (option) => String(option.userId));
}

export function requireWorkItemStatusOptions(value: unknown):
    AssessmentWorkItemOffsetPage<AssessmentWorkItemStatusOption> {
    return requireOffsetPage(value, (entry) => {
        const option = record(entry, "status option");
        const status = option["value"];
        if (!isWorkItemStatusSelector(status)) {
            throw new AssessmentWorkItemApiError("Invalid work-item status option response.");
        }
        return { value: status, label: nonemptyString(option["label"], "status option label") };
    }, (option) => option.value);
}

export function requireWorkItemCount(value: unknown): AssessmentWorkItemCount {
    const count = record(value, "work-item count");
    return { count: nonnegativeInteger(count["count"], "work-item count") };
}

export function requireWorkItemEvidenceDetail(value: unknown, requestedLicenseId: number,
    requestedEvidenceId: number): AssessmentWorkItemEvidenceDetail {
    const body = record(value, "exact evidence detail");
    const rawEvidence = record(body["evidence"], "exact evidence detail");
    const summary = requireEvidenceSummary(rawEvidence);
    if (summary.productLicenseId !== requestedLicenseId || summary.id !== requestedEvidenceId) {
        throw new AssessmentWorkItemApiError("Invalid exact evidence target response.");
    }

    return summary;
}
